import React, { useMemo } from "react";

import {
  faCalendarClock,
  faQuestion,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { endOfDay, startOfDay, subYears } from "date-fns";

import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { useCase } from "src/cases/queries/cases";
import {
  CaseEnvironment,
  EventViewerCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { EmptyState } from "src/design-system/EmptyState";
import { Cardinality, useEntitySchemas } from "src/entities/queries";
import { useEventEntityDisplayMap } from "src/entities/useEntityDisplayValues";
import { SensitiveEventPropertyField } from "src/entitiesEventsCommon/SensitivePropertyField";
import { isSensitiveType } from "src/entitiesEventsCommon/sensitiveTypes";
import {
  useEvent,
  useEventByIdAsync,
  useEventSchema,
} from "src/eventsCatalogue/queries";
import { RelationIcon } from "src/eventsCatalogue/SchemaEditor/RelationIcons";
import { PROPERTY_TYPES_TO_ICONS } from "src/eventsCatalogue/SchemaEditor/utils";
import { renderEventPropertyValue } from "src/eventsCatalogue/utils/renderEventValue";
import { Environment } from "src/hooks/useEnvironment";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasePageParams } from "src/router/urls";
import { TYPE_ICONS } from "src/utils/constants";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EventViewerBlockProps = Omit<CaseBlockProps, "children"> & {
  data: EventViewerCaseBlockConfigAttributes;
  caseEnv?: CaseEnvironment;
};

export const EventDetailsBlock: React.FC<EventViewerBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const { workspace } = useWorkspaceContext();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();

  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";

  const { data: entitySchemas, isLoading: isLoadingEntitySchemas } =
    useEntitySchemas({
      baseUrl: workspace.base_url ?? "",
    });

  const { data: eventSchema, isLoading: isLoadingSchema } = useEventSchema(
    data.event.type,
  );

  const isAsyncFetchEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.eventDetailsAsyncFetch,
  );

  const { data: caseData } = useCase(caseId);
  const caseCreatedAt = caseData?.data.created_at;

  const timeWindow = useMemo(() => {
    const anchor = caseCreatedAt ? new Date(caseCreatedAt) : new Date();
    return {
      from: subYears(startOfDay(anchor), 1).toISOString(),
      to: endOfDay(anchor).toISOString(),
    };
  }, [caseCreatedAt]);

  const eventParams = {
    eventType: data.event.type,
    eventId: data.event.id,
    environment: env as Environment,
    timeWindow,
  };

  // Feature-flag controlled; defaults to the sync GET-by-id path, opting into submit/poll when enabled.
  const syncEventQuery = useEvent(eventParams, {
    enabled: !!data.event.id && !isAsyncFetchEnabled,
  });
  const asyncEventQuery = useEventByIdAsync(eventParams, {
    enabled: !!data.event.id && isAsyncFetchEnabled,
    allowCachedExecution: true,
  });

  const eventQuery = isAsyncFetchEnabled ? asyncEventQuery : syncEventQuery;
  const { data: event, isLoading: isLoadingEvent, isEnabled } = eventQuery;

  const { displayMap: entityDisplayMap } = useEventEntityDisplayMap({
    eventSchema,
    events: event ? [event] : [],
    environment: env as Environment,
  });

  const items: PropertiesListItem[] = useMemo(() => {
    if (!event?.payload || !eventSchema) return [];

    const eventData = event.payload;
    const rows: PropertiesListItem[] = [];

    rows.push({
      key: "id",
      label: "ID",
      value: (
        <TruncatedIdPill feedback="inline-only-checkmark" id={eventData.id} />
      ),
      icon: TYPE_ICONS.string || faQuestion,
    });

    Object.entries(eventSchema.properties)
      .filter(([key]) => !["id"].includes(key))
      .forEach(([key, propertyDef]) => {
        const cellValue = eventData[key];
        if (cellValue === undefined || cellValue === null) return;

        const icon =
          propertyDef.type === PropertyDefinitionOutputTypeEnum.RELATION ? (
            <RelationIcon cardinality={Cardinality.ONE} size="2xs" />
          ) : (
            PROPERTY_TYPES_TO_ICONS[
              propertyDef.type as keyof typeof PROPERTY_TYPES_TO_ICONS
            ] || faQuestion
          );

        const label =
          propertyDef.display_name ??
          propertyDef.relation_schema?.entity_type ??
          key;

        const renderedValue =
          isSensitiveType(propertyDef.type) && propertyDef._sensitive ? (
            <SensitiveEventPropertyField
              env={env}
              eventId={eventData.id}
              eventType={data.event.type}
              field={key}
              propertyName={label}
              timestamp={eventData.timestamp ?? null}
              value={cellValue}
            />
          ) : (
            renderEventPropertyValue(
              cellValue,
              propertyDef,
              caseEnv as Environment,
              entitySchemas,
              key,
              entityDisplayMap,
            )
          );

        rows.push({
          key,
          label,
          value: renderedValue,
          icon,
        });
      });

    return rows;
  }, [
    event,
    eventSchema,
    entitySchemas,
    caseEnv,
    entityDisplayMap,
    env,
    data.event.type,
  ]);

  const isLoading = isLoadingSchema || isLoadingEvent || isLoadingEntitySchemas;

  if (!isEnabled) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="event-details-no-event-id"
          description="This block currently has no linked event."
          headline="No event provided"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (!isLoading && !event) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          dataLoc="event-details-error"
          description="Unable to load event data"
          headline="Error loading event"
          icon={faCalendarClock}
          size="compact"
          variant="error"
        />
      </CaseBlock>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          description="This event has no data to display"
          headline="No data available"
          icon={faCalendarClock}
          size="compact"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock {...props} isLoading={isLoading}>
      <PropertiesList
        dataLocPrefix="event-viewer-block"
        isLoading={isLoading}
        items={items}
        showIcons={true}
      />
    </CaseBlock>
  );
};
